import React, { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with an inline show/hide toggle.
 * Drop-in replacement for <input type="password" ... />.
 *
 * Preserves all standard input props (value, onChange, required, minLength,
 * autoComplete, placeholder, className, style, name, id, data-testid, etc.).
 * The `data-testid` on the input is preserved as-is; the toggle button gets
 * its own testid derived from it (`${testid}-toggle`) so tests can reach both.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = "", style = {}, ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);
  const testid = rest["data-testid"];
  const toggleTestid = testid ? `${testid}-toggle` : undefined;
  const Icon = visible ? EyeOff : Eye;
  return (
    <div className="relative">
      <input
        {...rest}
        ref={ref}
        type={visible ? "text" : "password"}
        className={className}
        style={{ ...style, paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={toggleTestid}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex items-center px-3 opacity-60 hover:opacity-100 transition-opacity"
        style={{ color: "var(--gf-text-muted, #6b7280)", background: "transparent" }}
      >
        <Icon size={18} strokeWidth={1.75} />
      </button>
    </div>
  );
});

export default PasswordInput;
